import { FoodRestriction, RecipeTag, User } from '@/api/types'
import { Label } from '@/components/Label'
import { RangeSlider } from '@/components/RangeSlider/RangeSlider'
import { Select } from '@/components/Select'
import { Tag, TagGroup } from '@/components/TagGroup'
import { RecipesSearch } from '@/routes/recipes'
import { t } from 'i18next'
import React, { useCallback, useMemo, useState } from 'react'
import { CUISINE, EXPERTISE_LEVELS, ExpertiseLevel, STATUS } from '../constants'
import { DietType, IntervalType, getUserFullName, groupTags } from '../helpers'
import './RecipesTableFilters.css'

type Props = {
  tags: RecipeTag[]
  allergens: FoodRestriction[]
  authors: User[]
  filters: Filters
  setSearchParams: (key: keyof RecipesSearch, value: string | number | undefined | boolean) => void
  setSearchParamsArray: (key: keyof RecipesSearch, selection: Set<string>) => void
  setSearchParamsObject: (obj: RecipesSearch) => void
}

export type Filters = {
  tagName?: string[] | undefined
  noMealTypes?: boolean | undefined
  noSeasons?: boolean | undefined
  authorId?: number | undefined
  suitableDiet?: string[] | undefined
  suitableFoodRestrictionID?: string[] | undefined
  status?: string | undefined
  cuisine?: string | undefined
  expertiseLevel?: ExpertiseLevel[] | undefined
  maxPreparationTime?: number | undefined
  maxCookingTime?: number | undefined
  minKcal?: number | undefined
  maxKcal?: number | undefined
  minProteinRatio?: number | undefined
  maxProteinRatio?: number | undefined
  minFatRatio?: number | undefined
  maxFatRatio?: number | undefined
  minCarbsRatio?: number | undefined
  maxCarbsRatio?: number | undefined
  minServings?: number | undefined
  maxServings?: number | undefined
  photoFilter?: string | undefined
}

const ALL_LABEL = t('filters.all')
const MIN_KCAL = 0
const MAX_KCAL = 1000
const MIN_SERVINGS = 1
const MAX_SERVINGS = 20
const NO_MEAL_TYPE = 'NO_MEAL_TYPE'
const NO_SEASON = 'NO_SEASON'

const SELECT_STYLE = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    paddingLeft: 12,
    borderColor: '#e9e9e9',
    backgroundColor: state.isFocused ? '#fff' : '#e9e9e9',
    borderRadius: 12,

    ':hover': {
      borderColor: '#e9e9e9',
    },
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
  }),
}

type TimeIntervalOption = {
  name: IntervalType
  value: number
}

const TIME_INTERVALS: TimeIntervalOption[] = [
  { name: 'LESS_15', value: 15 },
  { name: 'LESS_30', value: 30 },
  { name: 'LESS_60', value: 60 },
]

function intersectTags(tags: string[], recipeTags: RecipeTag[]): string[] {
  return tags.filter((tag) => recipeTags.some((recipeTag) => recipeTag.name === tag))
}

export default function RecipesTableFilters({
  tags,
  allergens,
  authors,
  filters,
  setSearchParams,
  setSearchParamsArray,
  setSearchParamsObject,
}: Props) {
  const groupedTags = useMemo(() => groupTags(tags), [tags])
  // read filters from URL
  const {
    tagName,
    noMealTypes,
    noSeasons,
    authorId,
    suitableDiet,
    suitableFoodRestrictionID,
    status,
    cuisine,
    expertiseLevel,
    maxPreparationTime,
    maxCookingTime,
    minKcal,
    maxKcal,
    minProteinRatio,
    maxProteinRatio,
    minFatRatio,
    maxFatRatio,
    minCarbsRatio,
    maxCarbsRatio,
    minServings,
    maxServings,
  } = filters

  const author = authors.find((author) => author.idUser === authorId) ?? null
  const authorLabel = author ? getUserFullName(author) : ALL_LABEL
  const [[minKcalState, maxKcalState], setMinMaxEnergy] = useState<[number, number]>([
    minKcal ?? MIN_KCAL,
    maxKcal ?? MAX_KCAL,
  ])

  const [[minProteinState, maxProteinState], setMinMaxProtein] = useState<[number, number]>([
    (minProteinRatio ?? 0) * 100,
    (maxProteinRatio ?? 1) * 100,
  ])

  const [[minFatState, maxFatState], setMinMaxFat] = useState<[number, number]>([
    (minFatRatio ?? 0) * 100,
    (maxFatRatio ?? 1) * 100,
  ])

  const [[minCarbsState, maxCarbsState], setMinMaxCarbs] = useState<[number, number]>([
    (minCarbsRatio ?? 0) * 100,
    (maxCarbsRatio ?? 1) * 100,
  ])

  const [[minServingsState, maxServingsState], setServingsMinMax] = useState<[number, number]>([
    minServings ?? MIN_SERVINGS,
    maxServings ?? MAX_SERVINGS,
  ])

  const statusOptions = useMemo(
    () =>
      STATUS.map((status: (typeof STATUS)[number]) => ({
        value: status,
        label: t(`recipe.status.${status}`),
      })),
    [],
  )

  const hasPhotoOptions = [
    {
      label: t('recipe.photoAll'),
      value: 'ALL',
    },
    {
      label: t('recipe.photoWith'),
      value: 'HAS_PHOTO',
    },
    {
      label: t('recipe.photoWithout'),
      value: 'NO_PHOTO',
    },
    {
      label: t('recipe.photoNeedsReview'),
      value: 'PENDING_REVIEW',
    },
  ]

  const mealTypesTags = useMemo(
    () => intersectTags(tagName ?? [], groupedTags.MEAL_TYPE),
    [tagName, groupedTags],
  )

  const selectedMealTypes = useMemo(() => {
    return [...mealTypesTags, ...(noMealTypes ? [NO_MEAL_TYPE] : [])]
  }, [mealTypesTags, noMealTypes])

  const seasonTags = useMemo(
    () => intersectTags(tagName ?? [], groupedTags.SEASON),
    [tagName, groupedTags],
  )

  const selectedSeasons = useMemo(() => {
    return [...seasonTags, ...(noSeasons ? [NO_SEASON] : [])]
  }, [seasonTags, noSeasons])

  const setRecipesEnergyRange = useCallback(
    (range: [number, number]) => {
      const [minKcal, maxKcal] = range

      setSearchParamsObject({
        minKcal: minKcal === MIN_KCAL ? undefined : minKcal,
        maxKcal: maxKcal === MAX_KCAL ? undefined : maxKcal,
      })
    },
    [setSearchParams],
  )

  const setRecipesProteinRange = useCallback(
    (range: [number, number]) => {
      const [minProtein, maxProtein] = range

      setSearchParamsObject({
        minProteinRatio: minProtein === 0 ? undefined : minProtein / 100,
        maxProteinRatio: maxProtein === 100 ? undefined : maxProtein / 100,
      })
    },
    [setSearchParams],
  )

  const setRecipesFatRange = useCallback(
    (range: [number, number]) => {
      const [minFat, maxFat] = range

      setSearchParamsObject({
        minFatRatio: minFat === 0 ? undefined : minFat / 100,
        maxFatRatio: maxFat === 100 ? undefined : maxFat / 100,
      })
    },
    [setSearchParams],
  )

  const setRecipesCarbsRange = useCallback(
    (range: [number, number]) => {
      const [minCarbs, maxCarbs] = range

      setSearchParamsObject({
        minCarbsRatio: minCarbs === 0 ? undefined : minCarbs / 100,
        maxCarbsRatio: maxCarbs === 100 ? undefined : maxCarbs / 100,
      })
    },
    [setSearchParams],
  )

  const setServingsRange = useCallback(
    (range: [number, number]) => {
      const [minServings, maxServings] = range

      setSearchParamsObject({
        minServings: minServings === MIN_SERVINGS ? undefined : minServings,
        maxServings: maxServings === MAX_SERVINGS ? undefined : maxServings,
      })
    },
    [setSearchParams],
  )

  return (
    <div className="RecipesTableQuickFilters">
      <h5>Filter by:</h5>
      <div className="FilterSection">
        <TagGroup
          label={t('recipe.mealType')}
          selectionMode="multiple"
          items={[...groupedTags.MEAL_TYPE, { name: NO_MEAL_TYPE }]}
          selectedKeys={selectedMealTypes}
          onSelectionChange={(selection: Set<string>) => {
            const newNoMealTypes = !noMealTypes && selection.has(NO_MEAL_TYPE) ? true : undefined
            let mealTypes: Set<string>
            if (newNoMealTypes) {
              mealTypes = new Set([NO_MEAL_TYPE])
            } else {
              selection.delete(NO_MEAL_TYPE)
              mealTypes = selection
            }

            const mealTypesTags: Set<string> =
              mealTypes.size == 0 || newNoMealTypes ? new Set() : mealTypes
            const allTags = new Set([...mealTypesTags, ...seasonTags])

            setSearchParamsArray('tagName', allTags)

            setSearchParams('noMealTypes', newNoMealTypes)
          }}>
          {(item: RecipeTag) => (
            <Tag id={item.name}>{t(`recipe.mealTypeOptions.${item.name}` as any)}</Tag>
          )}
        </TagGroup>
      </div>
      <div className="FilterSection">
        <Label>{t('recipe.author')}</Label>
        <Select
          isClearable
          value={{
            value: author?.idUser,
            label: authorLabel,
          }}
          options={
            authors?.map((author) => ({
              value: author.idUser,
              label: getUserFullName(author),
            })) ?? []
          }
          styles={SELECT_STYLE}
          onChange={(selection) => {
            setSearchParams('authorId', selection?.value)
          }}></Select>
      </div>
      <div className="FilterSection">
        <Label>Status</Label>
        <Select
          value={{ value: status, label: status ? t(`recipe.status.${status}` as any) : ALL_LABEL }}
          isClearable
          options={statusOptions}
          onChange={(selection) => {
            setSearchParams('status', selection?.value)
          }}
          styles={SELECT_STYLE}
        />
      </div>
      <div className="FilterSection">
        <Label>{t('recipe.cuisine')}</Label>
        <Select
          isClearable
          styles={SELECT_STYLE}
          getOptionLabel={(option) =>
            option?.name ? t(`recipe.cuisines.${option.name}` as any) : ALL_LABEL
          }
          getOptionValue={(option) => option.name as string}
          onChange={(selection) => {
            setSearchParams('cuisine', selection?.name)
          }}
          value={{ name: cuisine }}
          options={CUISINE.map((cuisine: (typeof CUISINE)[number]) => ({
            name: cuisine,
          }))}
        />
      </div>
      <div className="FilterSection">
        <TagGroup
          label={t('recipe.diet')}
          selectionMode="multiple"
          items={groupedTags.DIET}
          selectedKeys={suitableDiet || []}
          onSelectionChange={(selection: Set<string>) => {
            setSearchParamsArray('suitableDiet', selection)
          }}>
          {(item) => <Tag id={item.name}>{t(`recipe.dietOptions.${item.name as DietType}`)}</Tag>}
        </TagGroup>
      </div>
      <div className="FilterSection">
        <TagGroup
          label={t('filters.allergensSuitable')}
          selectionMode="multiple"
          items={allergens}
          selectedKeys={suitableFoodRestrictionID || []}
          onSelectionChange={(selection: Set<string>) => {
            setSearchParamsArray('suitableFoodRestrictionID', selection)
          }}>
          {(item) => <Tag id={item.idFoodRestriction}>{item.name}</Tag>}
        </TagGroup>
      </div>
      <div className="FilterSection">
        <TagGroup
          label={t('recipe.season')}
          selectionMode="multiple"
          items={[...groupedTags.SEASON, { name: NO_SEASON }]}
          selectedKeys={selectedSeasons}
          onSelectionChange={(selection: Set<string>) => {
            const newNoSeasons = !noSeasons && selection.has(NO_SEASON) ? true : undefined
            let seasons: Set<string>
            if (newNoSeasons) {
              seasons = new Set([NO_SEASON])
            } else {
              selection.delete(NO_SEASON)
              seasons = selection
            }
            const seasonsTags: Set<string> = seasons.size == 0 || newNoSeasons ? new Set() : seasons
            const allTags = new Set([...mealTypesTags, ...seasonsTags])

            setSearchParamsArray('tagName', allTags)

            setSearchParams('noSeasons', newNoSeasons)
          }}>
          {(item: RecipeTag) => (
            <Tag id={item.name}>{t(`recipe.seasonOptions.${item.name}` as any)}</Tag>
          )}
        </TagGroup>
      </div>

      <div className="FilterSection">
        <TagGroup
          label={t('recipe.expertise')}
          selectionMode="multiple"
          items={EXPERTISE_LEVELS.map((level) => ({
            name: level,
          }))}
          selectedKeys={expertiseLevel || []}
          onSelectionChange={(selection: Set<ExpertiseLevel>) => {
            setSearchParamsArray('expertiseLevel', selection)
          }}>
          {(item) => <Tag id={item.name}>{t(`recipe.expertiseOptions.${item.name}`)}</Tag>}
        </TagGroup>
      </div>
      <div className="FilterSection">
        <Label>{t('recipe.photo')}</Label>
        <Select
          value={hasPhotoOptions.find((option) => option.value === filters.photoFilter)}
          options={hasPhotoOptions}
          onChange={(selection) => {
            setSearchParams('photoFilter', selection?.value)
          }}
          styles={SELECT_STYLE}
        />
      </div>
      <div className="FilterSection">
        <RangeSlider
          label={t('filters.servings')}
          value={[minServingsState ?? MIN_SERVINGS, maxServingsState ?? MAX_SERVINGS]}
          onChange={(val) => setServingsMinMax(val as [number, number])}
          onChangeEnd={setServingsRange}
          minValue={MIN_SERVINGS}
          maxValue={MAX_SERVINGS}
          maxValueSuffix="+"
        />
      </div>
      <div className="FilterSection">
        <TagGroup
          label={t('filters.preparationTime')}
          selectionMode="single"
          items={TIME_INTERVALS}
          selectedKeys={TIME_INTERVALS.filter((item) => maxPreparationTime === item.value).map(
            (item) => item.name,
          )}
          onSelectionChange={(selection: Set<string>) => {
            const value = selection.size > 0 ? selection.values().next().value : undefined
            const maxPreparationTime = value
              ? TIME_INTERVALS.find((item) => item.name === value)?.value
              : undefined

            setSearchParams('maxPreparationTime', maxPreparationTime)
          }}>
          {(item) => <Tag id={item.name}>{t(`filters.intervalOptions.${item.name}`)}</Tag>}
        </TagGroup>
      </div>
      <div className="FilterSection">
        <TagGroup
          label={t('filters.cookingTime')}
          selectionMode="single"
          items={TIME_INTERVALS}
          selectedKeys={TIME_INTERVALS.filter((item) => maxCookingTime === item.value).map(
            (item) => item.name,
          )}
          onSelectionChange={(selection: Set<string>) => {
            const value = selection.size > 0 ? selection.values().next().value : undefined

            const maxCookingTime = value
              ? TIME_INTERVALS.find((item) => item.name === value)?.value
              : undefined

            setSearchParams('maxCookingTime', maxCookingTime)
          }}>
          {(item) => <Tag id={item.name}>{t(`filters.intervalOptions.${item.name}`)}</Tag>}
        </TagGroup>
      </div>
      <div className="FilterSection">
        <RangeSlider
          label={t('filters.caloriesPerServing')}
          value={[minKcalState ?? MIN_KCAL, maxKcalState ?? MAX_KCAL]}
          onChange={(val) => setMinMaxEnergy(val as [number, number])}
          onChangeEnd={setRecipesEnergyRange}
          minValue={MIN_KCAL}
          maxValue={MAX_KCAL}
          maxValueSuffix="+"
        />
      </div>
      <div className="FilterSection">
        <RangeSlider
          label={t('filters.proteinRatio')}
          value={[minProteinState ?? 0, maxProteinState ?? 100]}
          onChange={(val) => setMinMaxProtein(val as [number, number])}
          onChangeEnd={setRecipesProteinRange}
          minValue={0}
          maxValue={100}
          maxValueSuffix="%"
        />
      </div>
      <div className="FilterSection">
        <RangeSlider
          label={t('filters.fatRatio')}
          value={[minFatState ?? 0, maxFatState ?? 100]}
          onChange={(val) => setMinMaxFat(val as [number, number])}
          onChangeEnd={setRecipesFatRange}
          minValue={0}
          maxValue={100}
          maxValueSuffix="%"
        />
      </div>
      <div className="FilterSection">
        <RangeSlider
          label={t('filters.carbsRatio')}
          value={[minCarbsState ?? 0, maxCarbsState ?? 100]}
          onChange={(val) => setMinMaxCarbs(val as [number, number])}
          onChangeEnd={setRecipesCarbsRange}
          minValue={0}
          maxValue={100}
          maxValueSuffix="%"
        />
      </div>
    </div>
  )
}
